import { Button, EmptyState, PageHeader } from '@mgrvtechgroup/admin-ds';
import { Building2Icon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { canReadDomain, skipAccessGuard } from '../api/core/authorization';
import { ShellHeader } from '../app/shell-header';
import { InlineLoading } from '../components/inline-loading';
import { operatorSession, useOperatorSession } from './runtime';
import type { OperatorSession } from './session';

// 인증·권한을 확인하는 동안(authenticating·checking)은 아래 로딩 화면이 맡는다 — 로딩에 EmptyState를 쓰지 않는다(DS).
// authenticating이 여기까지 오는 것은 로그인을 시작하지 못했을 때뿐이다.
const titles = {
  'not-configured': '로그인 설정이 필요합니다',
  unauthenticated: '로그인이 필요합니다',
  authenticating: '로그인이 필요합니다',
  checking: '',
  authorized: '',
  forbidden: '운영자 권한이 없습니다',
  'service-failure': '업무 권한을 확인하지 못했습니다',
};
const automaticLoginAttempts = new WeakMap<OperatorSession, number>();
export const browserNavigation = {
  assign: (url: string) => {
    window.location.assign(url);
  },
};

export function PropertyAccessGuard({
  session = operatorSession,
  domain = 'property',
  requiredBits = 1,
  areaName = '지점',
  indexPath = '/properties',
  indexLabel = '지점 목록으로',
}: {
  session?: OperatorSession;
  domain?: string;
  requiredBits?: number;
  areaName?: string;
  indexPath?: string;
  indexLabel?: string;
}) {
  const state = useOperatorSession(session);
  const location = useLocation();
  const [loginError, setLoginError] = useState(false);
  const propertyDenied =
    !skipAccessGuard &&
    state.status === 'authorized' &&
    (!state.authorization ||
      !canReadDomain(state.authorization, domain, requiredBits) ||
      state.deniedDomains.includes(domain));
  const returnTo = location.pathname + location.search;
  const startLogin = useCallback(() => {
    const generation = session.getSnapshot().generation + 1;
    return session
      .beginLogin(returnTo, window.location.origin)
      .then((url) => {
        const current = session.getSnapshot();
        if (current.generation === generation && current.status === 'authenticating')
          browserNavigation.assign(url);
      })
      .catch(() => {
        const current = session.getSnapshot();
        if (
          current.generation === generation ||
          (current.reason === 'login-start-failed' && current.generation === generation + 1)
        )
          setLoginError(true);
      });
  }, [returnTo, session]);
  useEffect(() => {
    if (
      state.status !== 'unauthenticated' ||
      state.generation !== 0 ||
      state.reason !== undefined ||
      automaticLoginAttempts.has(session)
    )
      return;
    automaticLoginAttempts.set(session, state.generation);
    // 새로고침·새 탭·새 창이면 저장된 refresh 토큰으로 로그인을 이어 받고, 못 이어 받을 때만 로그인을 시작한다.
    void session.restore().then((restored) => {
      if (!restored) void startLogin();
    });
  }, [session, startLogin, state.generation, state.reason, state.status]);
  if (state.status === 'authorized' && !propertyDenied) return <Outlet />;
  const entering =
    state.status === 'authenticating' ||
    (state.status === 'unauthenticated' && state.generation === 0 && !state.reason);
  if (entering && !loginError)
    return (
      <div className="p-6">
        <InlineLoading>인증을 확인하고 있습니다.</InlineLoading>
      </div>
    );
  const header = (
    <ShellHeader>
      <PageHeader
        title={location.pathname === indexPath ? areaName : `${areaName} 상세`}
        className="items-start sm:items-center"
      />
    </ShellHeader>
  );
  // 권한을 확인하는 동안에도 실패·거부 화면과 같은 머리·자리를 쓴다.
  if (state.status === 'checking')
    return (
      <>
        {header}
        <div className="p-4 sm:p-6">
          <InlineLoading>업무 권한을 확인하고 있습니다.</InlineLoading>
        </div>
      </>
    );
  const detail =
    state.status === 'not-configured'
      ? '로그인 설정이 완료되지 않았습니다. 관리자에게 문의해 주세요.'
      : state.status === 'forbidden' || propertyDenied
        ? `${areaName} 조회 권한이 없습니다. 관리자에게 권한을 확인해 주세요.`
        : state.status === 'service-failure'
          ? '업무 권한을 확인하는 중 오류가 발생했습니다. 잠시 후 다시 확인해 주세요.'
          : state.reason === 'expired' || state.reason === 'failed-refresh'
            ? '세션이 만료되었습니다. 다시 로그인해 주세요.'
            : state.reason === 'invalid-callback'
              ? '로그인 정보를 확인하지 못했습니다. 다시 로그인해 주세요.'
              : '로그인한 뒤에 이용할 수 있습니다.';
  return (
    <>
      {header}
      <div className="flex min-w-0 flex-col gap-4 p-4 sm:p-6" aria-live="polite">
        <EmptyState
          icon={Building2Icon}
          title={
            propertyDenied
              ? `${areaName} 조회 권한이 없습니다`
              : state.status === 'forbidden'
                ? titles.forbidden
                : titles[state.status]
          }
          description={detail}
        />
        {loginError && (
          <p role="alert" className="body-sm text-destructive">
            로그인을 시작하지 못했습니다. 잠시 후 다시 시도해 주세요.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {loginError && <Button onClick={() => void startLogin()}>로그인 다시 시도</Button>}
          {(state.status === 'service-failure' ||
            state.status === 'forbidden' ||
            propertyDenied) && (
            <Button
              onClick={() => {
                void session.loadCurrentAuthorization();
              }}
            >
              권한 다시 확인
            </Button>
          )}
          {location.pathname !== indexPath && (
            <Button
              nativeButton={false}
              role="link"
              variant="outline"
              render={<Link to={indexPath} />}
            >
              {indexLabel}
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
